import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import {
  ChromeHeaderData,
  ChromeSharedData,
} from '@amzn/aac-chrome-internal-api';
import { ErrorIdProvider } from '@amzn/storm-ui';
import { initHeaderState } from 'src/features/header/store/slice';
import { markComponentStart } from 'src/commons/utils/logWithTakt';
import { CHROME_APP_NAME, Components } from 'src/commons/constants';
import { performanceMark } from 'src/utils/performance-helper';
import { store } from 'src/app/store';
import { InternalChromeConfigContextProvider } from 'src/providers/InternalChromeConfigContextProvider';
import { ChromeConfigContextProvider } from 'src/providers/ChromeConfigContextProvider';
import { ConnectedChromeApp } from 'src/commons/components/ChromeApp/ChromeApp';
import { ConnectedGlobalHeader } from 'src/features/header/GlobalHeader';
import {
  isServerAssembledPage,
  isWebComponentRenderingHidden,
} from 'src/commons/utils';
import {
  HeaderBundleProvider,
  PageHeaderBundleProvider,
} from 'src/providers/PantherLocalizationProvider';
import { GlobalHeaderMetricPublisher } from 'src/commons/utils/metrics';
import { markComponentRendered } from 'src/utils/component-initialization';
import { loadHeaderStateFromChromeData } from 'src/web-components/shared/loadHeaderStateFromChromeData';

/**
 * The global header bar. On Trim and BTSW web-component pages the server sets
 * `initChromeHeaderConfig` / `initChromeSharedConfig`; on any other page the
 * element loads ChromeData itself. Its layout offsets `#sc-content-container`
 * below the fixed header, so a page outside Trim puts
 * `ax-chrome-marketplace-switcher` and `ax-chrome-page-header` in that
 * element, the way BTSW assembles its pages:
 *
 *   <ax-chrome-global-header></ax-chrome-global-header>
 *   <div id="sc-content-container">
 *     <ax-chrome-marketplace-switcher></ax-chrome-marketplace-switcher>
 *     <ax-chrome-page-header></ax-chrome-page-header>
 *   </div>
 *   <ax-chrome-nav></ax-chrome-nav>
 *   ...
 */
export default class AXChromeGlobalHeader extends HTMLElement {
  static observedAttributes = [
    'initChromeSharedConfig',
    'initChromeHeaderConfig',
  ];
  _initChromeSharedConfigProperty = this['initChromeSharedConfig'] ?? {};
  _initChromeHeaderConfigProperty = this['initChromeHeaderConfig'] ?? {};

  constructor() {
    super();
  }

  set initChromeSharedConfig(initChromeSharedConfig: ChromeSharedData) {
    if (initChromeSharedConfig) {
      this._initChromeSharedConfigProperty = initChromeSharedConfig;
      this.updateHeaderStore();
    }
  }

  set initChromeHeaderConfig(initChromeHeaderConfig: ChromeHeaderData) {
    if (initChromeHeaderConfig) {
      this._initChromeHeaderConfigProperty = initChromeHeaderConfig;
      this.updateHeaderStore();
    }
  }

  updateHeaderStore() {
    const trimHeaderState = {
      ...this._initChromeHeaderConfigProperty,
      ...this._initChromeSharedConfigProperty,
    };
    store.dispatch(initHeaderState(trimHeaderState));
  }

  hasSuppliedConfig(): boolean {
    return (
      Object.keys(this._initChromeHeaderConfigProperty).length > 0 ||
      Object.keys(this._initChromeSharedConfigProperty).length > 0
    );
  }

  async _renderGlobalHeader() {
    const serverAssembled = isServerAssembledPage();
    if (!serverAssembled && (await isWebComponentRenderingHidden())) {
      return;
    }
    markComponentStart(Components.GLOBAL_HEADER);
    const endMarker = performanceMark('aac-chrome-ui-global-header');

    this.updateHeaderStore();
    // Outside Trim and BTSW nothing hands this element its config, so load it
    // the way <ax-chrome-header> does. A page that sets the config properties
    // itself before the element connects keeps them. On Trim and BTSW the
    // config is set by an inline script AFTER the element connects, so "no
    // config yet" there does not mean "fetch".
    if (!serverAssembled && !this.hasSuppliedConfig()) {
      loadHeaderStateFromChromeData().then((loaded) =>
        GlobalHeaderMetricPublisher.publishCounterMonitor(
          'initFailure',
          loaded ? 0 : 1,
        ),
      );
    }

    ReactDOM.render(
      <ErrorIdProvider id="Chrome">
        <Provider store={store}>
          <InternalChromeConfigContextProvider>
            <ChromeConfigContextProvider>
              <ConnectedChromeApp name={CHROME_APP_NAME.GlobalHeader}>
                <HeaderBundleProvider>
                  <PageHeaderBundleProvider>
                    <ConnectedGlobalHeader />
                  </PageHeaderBundleProvider>
                </HeaderBundleProvider>
              </ConnectedChromeApp>
            </ChromeConfigContextProvider>
          </InternalChromeConfigContextProvider>
        </Provider>
      </ErrorIdProvider>,
      this,
    );

    endMarker();
  }

  connectedCallback(): void {
    this._renderGlobalHeader();
    markComponentRendered('header');
  }
}

if (!customElements.get('ax-chrome-global-header')) {
  customElements.define('ax-chrome-global-header', AXChromeGlobalHeader);
}
